<template>
  <div>
    <v-banner
      v-bind="{ dark }"
      single-line
    >
      <span class="font-weight-bold">{{ checklist.title }}</span>

      <template #actions>
        <v-tooltip
          v-if="hasFinishedItems"
          bottom
        >
          <template #activator="{on, attrs}">
            <v-btn
              icon
              small
              v-bind="attrs"
              v-on="on"
              @click="toggleItemVisibility"
            >
              <v-icon v-if="hideFinishedItems">
                visibility
              </v-icon>
              <v-icon v-else>
                visibility_off
              </v-icon>
            </v-btn>
          </template>
          <span v-if="hideFinishedItems">Show all</span>
          <span v-else>View incomplete only</span>
        </v-tooltip>
      </template>
    </v-banner>

    <v-list
      class="py-0"
      color="transparent"
      v-bind="{ dark }"
      dense
    >
      <div
        v-for="(item) of visibleItems"
        :key="item.id"
      >
        <v-divider />

        <v-list-item
          dense
        >
          <v-list-item-action
            class="text-center"
          >
            <v-scroll-x-transition
              group
              leave-absolute
            >
              <v-progress-circular
                v-if="item.togglingValue"
                key="loading"
                color="primary"
                indeterminate
                size="24"
              />
              <v-checkbox
                v-else
                key="done"
                v-model="item.done"
                :disabled="disabled"
                @click="_ => toggleChecklistItem(item.id)"
              />
            </v-scroll-x-transition>
          </v-list-item-action>

          <v-list-item-content>
            {{ item.name }}
          </v-list-item-content>
        </v-list-item>
      </div>

      <div
        v-if="!hasUnfinishedItems && hideFinishedItems"
      >
        <v-divider />

        <v-list-item>
          <v-list-item-content>
            <v-list-item-title
              class="mt-3 text-center"
            >
              Congratulations, all items are complete!
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </div>
    </v-list>

    <v-card-actions
      v-if="canCollapse"
      class="read-more-container"
      :class="[!showCollapsed ? 'expanded' : '']"
    >
      <div
        class="text-caption"
      >
        Showing {{ visibleItems.length }} of {{ displayableItems.length }} items
      </div>

      <v-spacer />

      <v-btn
        icon
        outlined
        v-bind="{ dark }"
        @click="toggleCollapsed"
      >
        <v-icon
          v-if="hasCollapsedItems"
          title="Expand"
        >
          expand_more
        </v-icon>
        <v-icon
          v-else
          title="Collapse"
        >
          expand_less
        </v-icon>
      </v-btn>
    </v-card-actions>
  </div>
</template>

<script>
import Vue from 'vue'

import { alertHandler } from '@/alertHandler'

const MAX_ITEMS_IN_NON_EXPANDED_MODE = 5

export default {
  name: 'ChecklistViewer',

  props: {
    checklist: {
      type: Object,
      required: true,
    },

    dark: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    expanded: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      allItems: [],
      hideFinishedItems: false,
      lastUsedItemId: 0,
      showCollapsed: false,
    }
  },

  computed: {
    canCollapse () {
      return this.displayableItems.length > MAX_ITEMS_IN_NON_EXPANDED_MODE
    },

    displayableItems () {
      return this.hideFinishedItems ? this.allItems.filter(item => !item.done) : this.allItems
    },

    finishedItemCount () {
      return this.allItems.filter(item => item.done).length
    },

    hasCollapsedItems () {
      return this.canCollapse && !(this.expanded || this.showCollapsed)
    },

    hasFinishedItems () {
      return this.finishedItemCount > 0
    },

    hasHiddenItems () {
      return !this.expanded && (this.hideFinishedItems || this.allItems.length > MAX_ITEMS_IN_NON_EXPANDED_MODE)
    },

    hasUnfinishedItems () {
      return this.unfinishedItemCount > 0
    },

    unfinishedItemCount () {
      return this.allItems.length - this.finishedItemCount
    },

    visibleItems () {
      return this.hasCollapsedItems
        ? this.displayableItems.slice(0, MAX_ITEMS_IN_NON_EXPANDED_MODE)
        : this.displayableItems
    },
  },

  watch: {
    checklist: {
      handler: 'reloadItems',
      deep: true,
    },
  },

  created () {
    this.reloadItems(this.checklist)
  },

  methods: {
    reloadItems (checklist) {
      if (checklist.items.length !== this.allItems.length) this.allItems = []

      checklist.items.forEach((item, index) => {
        if (this.allItems[index]) {
          const localItem = this.allItems[index]
          if (!localItem.togglingValue) {
            localItem.done = item.done
            localItem.name = item.name
          }
        } else {
          Vue.set(this.allItems, index, {
            done: item.done,
            id: this.lastUsedItemId++,
            name: item.name,
            togglingValue: false,
          })
        }
      })
    },

    toggleChecklistItem (id) {
      const selectedItem = this.allItems.find(item => item.id === id)
      if (!selectedItem) return this.displayAlert('error', 'Item not found on checklist')

      selectedItem.togglingValue = true

      this.$emit('update', {
        data: {
          checklist: {
            items: this.allItems.map(item => {
              return {
                done: item.done,
                name: item.name,
              }
            }),
            title: this.checklist.title,
          },
        },
        errorCallback: error => {
          if (error.response && error.response.status === 401) return this.$router.push('/login')
          selectedItem.done = !selectedItem.done
          this.displayAlert('error', 'There was an error updating the checklist item', error)
        },
        finalCallback: _ => {
          selectedItem.togglingValue = false
        },
      })
    },

    toggleCollapsed () {
      this.showCollapsed = !this.showCollapsed
    },

    toggleItemVisibility () {
      this.hideFinishedItems = !this.hideFinishedItems
    },
  },
}
</script>
